import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Chip,
  Divider,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";

import { GetItemsByLocation } from "../../../../api/ComplaintApi";

function ComplaintProductStep({
  token,
  noEmplacement,

  selectedItem,
  selectedItemData,
  onSelectedItemChange,

  t,
  userLang = "fr",

  showValidation = false,
}) {
  // =========================================================
  // STATE
  // =========================================================

  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  // false = 6 derniers mois
  // true = tous les items
  const [showAll, setShowAll] = useState(false);

  const [error, setError] = useState(false);

  // =========================================================
  // LOAD ITEMS
  // =========================================================

  useEffect(() => {
    if (!token || !noEmplacement) {
      setItems([]);
      return;
    }

    const loadItems = async () => {
      try {
        setLoading(true);
        setError(false);

        const result = await GetItemsByLocation(token, noEmplacement, showAll);

        console.log("ITEMS :", result);

        setItems(result || []);
      } catch (err) {
        console.error("Unable to load items:", err);

        setItems([]);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadItems();
  }, [token, noEmplacement, showAll]);

  // =========================================================
  // ITEM NAME
  // =========================================================

  const getItemName = (item) => {
    if (!item) {
      return "";
    }

    if (userLang?.toLowerCase().startsWith("fr")) {
      return item.nomFr || item.nomFrItem || item.nomEn || item.nomEnItem || "";
    }

    return item.nomEn || item.nomEnItem || item.nomFr || item.nomFrItem || "";
  };

  // =========================================================
  // ITEM ID
  // =========================================================

  const getItemId = (item) => {
    return item?.idItem ?? item?.id ?? "";
  };

  // =========================================================
  // ORDER ID
  // =========================================================

  const getOrderId = (item) => {
    return item?.idCommande ?? item?.noCommande ?? "";
  };

  // =========================================================
  // EMPLOYEE
  // =========================================================

  const getEmployeeName = (item) => {
    return item?.nomEmploye ?? "";
  };

  // =========================================================
  // DATE
  // =========================================================

  const getCreationDate = (item) => {
    if (!item?.dateCreation) {
      return "";
    }

    const date = new Date(item.dateCreation);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString(
      userLang?.toLowerCase().startsWith("fr") ? "fr-CA" : "en-CA",
      {
        year: "numeric",
        month: "short",
        day: "numeric",
      },
    );
  };

  // =========================================================
  // CATEGORY
  // =========================================================

  const getCategoryName = (item) => {
    if (!item) {
      return "";
    }

    if (userLang?.toLowerCase().startsWith("fr")) {
      return item.nomFrCategorie || item.nomEnCategorie || "";
    }

    return item.nomEnCategorie || item.nomFrCategorie || "";
  };

  // =========================================================
  // SEARCH
  // =========================================================

  const displayedItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return items;
    }

    return items.filter((item) => {
      const itemId = String(getItemId(item)).toLowerCase();

      const orderId = String(getOrderId(item)).toLowerCase();

      const itemName = String(getItemName(item)).toLowerCase();

      const employee = String(getEmployeeName(item)).toLowerCase();

      const category = String(getCategoryName(item)).toLowerCase();

      return (
        itemId.includes(normalizedSearch) ||
        orderId.includes(normalizedSearch) ||
        itemName.includes(normalizedSearch) ||
        employee.includes(normalizedSearch) ||
        category.includes(normalizedSearch)
      );
    });
  }, [items, search, userLang]);

  // =========================================================
  // SELECT ITEM
  // =========================================================

  const handleSelectItem = (item) => {
    const itemId = getItemId(item);

    console.log("SELECTED ITEM ID :", itemId);
    console.log("SELECTED ITEM DATA :", item);

    onSelectedItemChange(itemId, item);
  };

  // =========================================================
  // SHOW ALL
  // =========================================================

  const handleShowAllChange = (event) => {
    setShowAll(event.target.checked);

    // Reset search
    setSearch("");
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Box>
      {/* =====================================================
          HEADER
      ===================================================== */}

      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          {t("Product")}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            maxWidth: 700,
            lineHeight: 1.6,
          }}
        >
          {t("SelectComplaintProduct")}
        </Typography>
      </Box>

      {/* =====================================================
          FILTER CARD
      ===================================================== */}

      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          mb: 2.5,
          borderRadius: 2,
          backgroundColor: "rgba(0, 0, 0, 0.015)",
        }}
      >
        {/* ---------------------------------------------------
            SEARCH
        --------------------------------------------------- */}

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          {t("SearchProduct")}
        </Typography>

        <TextField
          fullWidth
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("SearchProduct")}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* ---------------------------------------------------
            SHOW ALL
        --------------------------------------------------- */}

        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: "1px solid",
            borderColor: "divider",

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            gap: 2,
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={700}>
              {showAll ? t("AllItems") : t("LastSixMonths")}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {showAll ? t("ShowingAllItems") : t("ShowingItemsLastSixMonths")}
            </Typography>
          </Box>

          <FormControlLabel
            sx={{
              m: 0,
              flexShrink: 0,
            }}
            control={
              <Switch checked={showAll} onChange={handleShowAllChange} />
            }
            label={
              <Typography variant="body2" fontWeight={600}>
                {t("ShowAllProducts")}
              </Typography>
            }
          />
        </Box>
      </Paper>

      {/* =====================================================
          CURRENT FILTER
      ===================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {t("Items")}
        </Typography>

        <Chip
          size="small"
          icon={showAll ? <PublicOutlinedIcon /> : <HistoryOutlinedIcon />}
          label={showAll ? t("AllItems") : t("LastSixMonths")}
          variant="outlined"
        />
      </Box>

      {/* =====================================================
          ITEM LIST
      ===================================================== */}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: "hidden",

          borderColor:
            showValidation && !selectedItemData ? "error.main" : "divider",

          borderWidth: showValidation && !selectedItemData ? 2 : 1,
        }}
      >
        {/* =================================================
            LOADING
        ================================================= */}

        {loading ? (
          <Box
            sx={{
              py: 7,
              px: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="body1" fontWeight={600} color="text.secondary">
              {t("Loading")}
            </Typography>
          </Box>
        ) : error ? (
          /* =================================================
             ERROR
          ================================================= */

          <Box
            sx={{
              py: 7,
              px: 3,
              textAlign: "center",
            }}
          >
            <Inventory2OutlinedIcon
              sx={{
                fontSize: 42,
                color: "error.main",
                mb: 1,
              }}
            />

            <Typography variant="body1" fontWeight={600} color="error.main">
              {t("UnableToLoadItems")}
            </Typography>
          </Box>
        ) : displayedItems.length === 0 ? (
          /* =================================================
             NO ITEMS
          ================================================= */

          <Box
            sx={{
              py: 7,
              px: 3,
              textAlign: "center",
            }}
          >
            <Inventory2OutlinedIcon
              sx={{
                fontSize: 42,
                color: "text.disabled",
                mb: 1,
              }}
            />

            <Typography variant="body1" fontWeight={600} color="text.secondary">
              {t("NoProductsFound")}
            </Typography>

            <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
              {t("TryAnotherSearch")}
            </Typography>
          </Box>
        ) : (
          /* =================================================
             ITEMS
          ================================================= */

          <List
            disablePadding
            sx={{
              maxHeight: 400,
              overflowY: "auto",
            }}
          >
            {displayedItems.map((item, index) => {
              const itemId = getItemId(item);

              const selected = selectedItem === itemId;

              return (
                <React.Fragment key={`${itemId}-${getOrderId(item)}-${index}`}>
                  <ListItemButton
                    selected={selected}
                    onClick={() => handleSelectItem(item)}
                    sx={{
                      px: 2.5,
                      py: 1.75,

                      "&.Mui-selected": {
                        backgroundColor: "rgba(25, 118, 210, 0.06)",
                      },

                      "&.Mui-selected:hover": {
                        backgroundColor: "rgba(25, 118, 210, 0.10)",
                      },
                    }}
                  >
                    {/* =======================================
                        ICON
                    ======================================= */}

                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 1.5,
                        mr: 2,
                        flexShrink: 0,

                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        backgroundColor: selected
                          ? "primary.main"
                          : "rgba(25, 118, 210, 0.08)",

                        color: selected ? "#fff" : "primary.main",
                      }}
                    >
                      <Inventory2OutlinedIcon />
                    </Box>

                    {/* =======================================
                        CONTENT
                    ======================================= */}

                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography fontWeight={700} variant="body2">
                            {getItemName(item)}
                          </Typography>

                          {selected && (
                            <Chip
                              icon={<CheckCircleRoundedIcon />}
                              label={t("Selected")}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            mt: 0.5,
                          }}
                        >
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            {t("Item")} #{itemId}
                            {getOrderId(item) &&
                              ` • ${t("Order")} #${getOrderId(item)}`}
                          </Typography>

                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                          >
                            {getCategoryName(item)}

                            {getCategoryName(item) && getEmployeeName(item)
                              ? " • "
                              : ""}

                            {getEmployeeName(item)}

                            {(getCategoryName(item) || getEmployeeName(item)) &&
                            getCreationDate(item)
                              ? " • "
                              : ""}

                            {getCreationDate(item)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>

                  {index < displayedItems.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>

      {/* =====================================================
          VALIDATION ERROR
      ===================================================== */}

      {showValidation && !selectedItemData && (
        <Typography
          variant="body2"
          sx={{
            mt: 1.5,
            color: "error.main",
            fontWeight: 600,
          }}
        >
          {t("ItemRequired")}
        </Typography>
      )}

      {/* =====================================================
          SELECTED ITEM
      ===================================================== */}

      {selectedItemData && (
        <Paper
          variant="outlined"
          sx={{
            mt: 2,
            p: 2,

            borderRadius: 2,

            borderColor: "primary.main",

            backgroundColor: "rgba(25, 118, 210, 0.04)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
            }}
          >
            <CheckCircleRoundedIcon color="primary" fontSize="small" />

            <Typography variant="caption" color="primary.main" fontWeight={700}>
              {t("SelectedItem")}
            </Typography>
          </Box>

          <Typography variant="body1" fontWeight={700}>
            {getItemName(selectedItemData)}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("Item")} #{getItemId(selectedItemData)}
            {getOrderId(selectedItemData) &&
              ` • ${t("Order")} #${getOrderId(selectedItemData)}`}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default ComplaintProductStep;
